// The screen's layout mode — one place that decides how Valor Web uses the space it is given, on any phone, tablet or
// PC, in portrait or landscape (Ben, Oct 8 2026: "everything dynamic"). Computed from the window size and the device's
// safe areas, written onto <html> before the first paint (classes + CSS variables) and kept current on resize/rotate.
//
//   phone   portrait phone (or any window under 700 px wide): the designed layout — one column, bottom nav, full header.
//   rail    700 px and wider, or a phone turned sideways: the five nav medallions move to a rail down the left edge,
//           cards flow into 1–4 columns (--cols), list screens split into list + open item (`split`), the map and the
//           city view fill the screen.
//   short   a phone turned sideways (landscape, 540 px tall or less): rail + a slim one-line header.
//
// The chrome is drawn in mockup units (--u = one pixel of the 864-wide mockups). On a phone --u follows the screen width
// as before; in rail mode it follows the column width, kept between a small-phone and a large-tablet size, so a card is
// always drawn at a size its design works at and the extra room becomes more columns rather than bigger chrome.
import { useEffect, useState } from "react";

export interface Layout {
  rail: boolean;
  short: boolean;
  /** list screens (Reports, Mail) show the list and the open item side by side */
  split: boolean;
  /** card columns on grid screens */
  cols: number;
  /** px per mockup unit */
  u: number;
  /** the rail's width in px (0 on a phone) */
  railW: number;
  /** content width in px (inside the rail, the safe areas and the side padding) */
  content: number;
  /** side padding in px */
  pad: number;
}

/** Below this width (portrait) the designed phone layout is kept. */
export const RAIL_MIN_W = 700;
/** A landscape screen this short or shorter is a phone on its side: slim header. */
export const SHORT_MAX_H = 540;
/** The narrowest card column the designs are drawn for (a 360-px phone, less its padding). */
const COL_MIN = 340;
/** Grid columns aim for this width; more room becomes more columns. */
const COL_TARGET = 440;
const GAP = 14;
/** Split panes need this much content width. */
const SPLIT_MIN = 900;

export function computeLayout(vw: number, vh: number, safeL = 0, safeR = 0): Layout {
  const short = vw > vh && vh <= SHORT_MAX_H;
  const rail = short || vw >= RAIL_MIN_W;
  if (!rail) {
    const u = Math.min(vw, 480) / 864;
    return { rail, short, split: false, cols: 1, u, railW: 0, content: Math.min(vw, 480) - 40 * u, pad: 20 * u };
  }
  const railW = short ? 76 : vw >= 1100 ? 112 : 100;
  const pad = short ? 12 : vw >= 1100 ? 28 : 20;
  const content = Math.max(280, vw - railW - safeL - safeR - pad * 2);
  let cols: number;
  if (short) cols = content >= COL_MIN * 2 + GAP ? 2 : 1;
  else cols = Math.max(1, Math.min(4, Math.floor((content + GAP) / (COL_TARGET + GAP))));
  const colW = (content - GAP * (cols - 1)) / cols;
  // a column is 824 mockup units wide on the phone (864 less the side padding); clamp so the chrome stays between a
  // small phone's and a large tablet's
  const u = Math.max(short ? 0.4 : 0.5, Math.min(short ? 480 / 864 : 0.64, colW / 824));
  const split = !short && content >= SPLIT_MIN;
  return { rail, short, split, cols, u, railW, content, pad };
}

// the safe-area insets, read from a probe (env() has no JS API)
let probe: HTMLDivElement | null = null;
function safeInsets(): [number, number] {
  if (typeof document === "undefined" || !document.body) return [0, 0];
  if (!probe) {
    probe = document.createElement("div");
    probe.setAttribute("aria-hidden", "true");
    probe.style.cssText = "position:fixed;left:0;top:0;width:0;height:0;visibility:hidden;pointer-events:none;padding-left:env(safe-area-inset-left,0px);padding-right:env(safe-area-inset-right,0px)";
    document.body.appendChild(probe);
  }
  const cs = getComputedStyle(probe);
  return [parseFloat(cs.paddingLeft) || 0, parseFloat(cs.paddingRight) || 0];
}

let current: Layout = computeLayout(typeof window !== "undefined" ? window.innerWidth : 390, typeof window !== "undefined" ? window.innerHeight : 844);
const listeners = new Set<(l: Layout) => void>();

function apply(l: Layout) {
  const root = document.documentElement;
  root.classList.toggle("lay-rail", l.rail);
  root.classList.toggle("lay-phone", !l.rail);
  root.classList.toggle("lay-short", l.short);
  root.classList.toggle("lay-split", l.split);
  const s = root.style;
  s.setProperty("--u", `${l.u.toFixed(5)}px`);
  s.setProperty("--cols", String(l.cols));
  s.setProperty("--rail-w", `${l.railW}px`);
  s.setProperty("--pad", `${l.pad.toFixed(2)}px`);
}

function update() {
  const [sl, sr] = safeInsets();
  const next = computeLayout(window.innerWidth, window.innerHeight, sl, sr);
  const same = (Object.keys(next) as (keyof Layout)[]).every((k) => next[k] === current[k]);
  if (same && document.documentElement.classList.contains(next.rail ? "lay-rail" : "lay-phone")) return;
  current = next;
  apply(next);
  listeners.forEach((f) => f(next));
}

// Before the first paint, like the text size (prefs.ts); then on every resize / rotation.
if (typeof window !== "undefined" && typeof document !== "undefined") {
  apply(current);
  const start = () => update();
  if (document.body) start();
  else document.addEventListener("DOMContentLoaded", start, { once: true });
  let raf = 0;
  let lastW = window.innerWidth;
  const onResize = () => {
    // an on-screen keyboard (Android resizes the window) changes only the height while a field has focus: keep the
    // layout as it is rather than flipping to the landscape one under the player's typing
    const typing = document.activeElement instanceof HTMLElement && /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement.tagName);
    if (typing && window.innerWidth === lastW) return;
    lastW = window.innerWidth;
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(update);
  };
  window.addEventListener("resize", onResize);
  window.addEventListener("orientationchange", onResize);
}

export const getLayout = () => current;

/** The current layout; re-renders when it changes (rotation, window resize). */
export function useLayout(): Layout {
  const [l, setL] = useState(current);
  useEffect(() => {
    const f = (n: Layout) => setL(n);
    listeners.add(f);
    if (current !== l) setL(current);
    return () => {
      listeners.delete(f);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return l;
}
